/** Lege defaults voor antwoorden-state — alle 25 keys uit AntwoordenJson.
 *
 * v2 (2026-06-01): vraagteksten en schaal-labels teruggebracht naar de
 * wetenschappelijke spec uit motera/baseline-vragenlijst-v1.md:
 *  - Bewegen: PROMIS Physical Function Short Form 4a
 *  - Werkvermogen: WAS (Work Ability Score)
 *  - Werkfunctioneren: WRFQ-5
 *  - Werkbeleving / presenteïsme: DSPS-6
 *
 * Veld-keys ongewijzigd t.o.v. v1 (database-compatible).
 */
export const EMPTY_ANTWOORDEN = {
  // intake (Step 1)
  leeftijd: "",
  geslacht: "",
  lengte_cm: "",
  gewicht_kg: "",
  functie_sector: "",
  uur_staan_lopen: "",
  type_werkschoen: "",
  voet_klachten_voorgeschiedenis: "",
  // pijn (Step 2)
  pijn_voet: null,
  pijn_locaties: [],
  pijn_frequentie: "",
  // bewegen (Step 3)
  bewegen_huishouden: null,
  bewegen_traplopen: null,
  bewegen_wandelen: null,
  bewegen_boodschappen: null,
  // vermoeidheid + werkvermogen (Step 4)
  vermoeidheid_einde_werkdag: null,
  werkvermogen: null,
  // werkfunctioneren (Step 5)
  werk_start: null,
  werk_fouten: null,
  werk_bewegingen: null,
  werk_concentratie: null,
  werk_multitasken: null,
  // werkbeleving (Step 6)
  werkbeleving_stress: null,
  werkbeleving_taken: null,
  werkbeleving_plezier: null,
  werkbeleving_zelfvertrouwen: null,
  werkbeleving_concentratie: null,
  werkbeleving_energie: null,
  // klachten elders (Step 7)
  klachten_elders: [],
  klachten_elders_anders: "",
};

export const VRAGEN_LABELS = {
  // Intake — onveranderd
  leeftijd: "Wat is uw leeftijd?",
  geslacht: "Wat is uw geslacht?",
  lengte_cm: "Wat is uw lengte (cm)?",
  gewicht_kg: "Wat is uw gewicht (kg)?",
  functie_sector: "Wat is uw functie of in welke sector werkt u?",
  uur_staan_lopen: "Hoeveel uur per werkdag staat of loopt u gemiddeld?",
  type_werkschoen: "Welk type werkschoen draagt u? (bijvoorbeeld veiligheidsschoen S3, sneaker, klomp)",
  voet_klachten_voorgeschiedenis: "Heeft u op dit moment voet-klachten of eerder gehad? Hoe lang al? Eerder zolen?",

  // Pijn — NRS-11 (Numeric Rating Scale) + locatie + frequentie
  pijn_voet: "Hoeveel pijn heeft u de afgelopen week gemiddeld in uw voeten ervaren?",
  pijn_locaties: "Waar zit de pijn in uw voet? (meerdere mogelijk)",
  pijn_frequentie: "Hoe vaak heeft u last van voet-pijn?",

  // Vermoeidheid — single-item op WAS-stijl
  vermoeidheid_einde_werkdag: "Hoe vermoeid voelt u zich aan het einde van een werkdag?",

  // Bewegen — PROMIS-PF-4a, herformulering
  bewegen_huishouden: "Zware fysieke taken thuis (bijvoorbeeld stofzuigen, dweilen, tuinwerk)",
  bewegen_traplopen: "Traplopen",
  bewegen_wandelen: "15 minuten achter elkaar wandelen",
  bewegen_boodschappen: "Dagelijkse boodschappen doen of winkelen",

  // Werkvermogen — WAS (Work Ability Score)
  werkvermogen: "Als uw werkvermogen op zijn allerbest ooit een 10 was, welk cijfer geeft u uw werkvermogen vandaag?",

  // Werkfunctioneren — WRFQ-5, herformulering
  werk_start: "Direct aan het werk gaan na aankomst op uw werkplek",
  werk_fouten: "Uw werk uitvoeren zonder fouten",
  werk_bewegingen: "Steeds dezelfde bewegingen tijdens uw werk maken",
  werk_concentratie: "Geconcentreerd blijven op uw werk",
  werk_multitasken: "Meerdere taken tegelijk doen",

  // Werkbeleving — DSPS-6, herformulering
  werkbeleving_stress: "De stress en eisen van mijn werk waren goed te hanteren.",
  werkbeleving_taken: "Ik kon moeilijke werktaken afmaken zoals ik wilde.",
  werkbeleving_plezier: "Ik haalde plezier uit mijn werk.",
  werkbeleving_zelfvertrouwen: "Ik voelde me zelfverzekerd over het afronden van werktaken.",
  werkbeleving_concentratie: "Op het werk kon ik me goed concentreren op mijn doelen.",
  werkbeleving_energie: "Ik voelde me energiek genoeg om al mijn werk te voltooien.",

  // Klachten elders in lichaam — multi-select (voet-keten literatuur, Menz 2016)
  klachten_elders: "Heeft u op dit moment ook klachten op andere plekken in uw lichaam? (meerdere mogelijk)",
  klachten_elders_anders: "Andere plek — welke?",
};

export const PIJN_LOCATIE_OPTIES = [
  { value: "hak", label: "Hak" },
  { value: "voorvoet", label: "Voorvoet" },
  { value: "midvoet", label: "Midvoet" },
  { value: "tenen", label: "Tenen" },
  { value: "enkel", label: "Enkel" },
  { value: "geen", label: "Geen pijn" },
];

export const PIJN_FREQUENTIE_OPTIES = [
  { value: "dagelijks", label: "Dagelijks" },
  { value: "wekelijks", label: "Een paar keer per week" },
  { value: "maandelijks", label: "Een paar keer per maand" },
  { value: "zelden", label: "Zelden" },
  { value: "nooit", label: "Nooit" },
];

export const KLACHTEN_ELDERS_OPTIES = [
  { value: "geen", label: "Helemaal geen klachten" },
  { value: "onderbeen", label: "Onderbeen" },
  { value: "knie", label: "Knie" },
  { value: "bovenbeen", label: "Bovenbeen" },
  { value: "heup", label: "Heup" },
  { value: "onderrug", label: "Onderrug" },
  { value: "bovenrug", label: "Bovenrug" },
  { value: "nek", label: "Nek" },
  { value: "anders", label: "Anders, namelijk…" },
];

// Intro-tekst per sectie (spec v1.1)
export const SECTIE_INTRO = {
  bewegen: "Hoeveel moeite had u de afgelopen week met de volgende dingen?",
  werkfunctioneren: "Hoe vaak had u in de afgelopen 4 weken moeite met de volgende dingen op uw werk?",
  werkbeleving: "Denk aan de afgelopen 4 weken. Geef per stelling aan in hoeverre die voor u klopte:",
};

// Schalen — per sectie ander framing (per gevalideerd instrument)
export const BEWEGEN_SCHAAL = {
  min: "geen moeite",
  max: "onmogelijk om te doen",
  labels: { 1: "geen moeite", 2: "een beetje moeite", 3: "behoorlijk wat moeite", 4: "veel moeite", 5: "onmogelijk om te doen" },
};

export const WERK_FREQUENTIE_SCHAAL = {
  min: "altijd",
  max: "nooit",
  labels: { 1: "altijd", 2: "vaak", 3: "soms", 4: "zelden", 5: "nooit" },
};

export const WERKBELEVING_SCHAAL = {
  min: "helemaal oneens",
  max: "helemaal eens",
  labels: { 1: "helemaal oneens", 2: "enigszins oneens", 3: "neutraal", 4: "enigszins eens", 5: "helemaal eens" },
};

// Backwards compat — generic 1-5 (niet meer gebruikt in nieuwe wizard maar exporteren voor zekerheid)
export const SCHAAL_5_LABELS = {
  1: "1", 2: "2", 3: "3", 4: "4", 5: "5",
};
